// src/pages/ShowArchiveCard.js
//
// Mobile card for every Show Archive tab (2026-10-22). Below 768px the
// detail pages render one card per show instead of the wide table, which
// otherwise needs a sideways swipe to reach venue / lineup / Attended?.
// Visual language borrowed from the Event Feed card (Home.js / Feed.js):
// same panel colours, purple header line, date line, "Venue in City, State,
// Country" line, festival link and bold lineup, with the action row below a
// divider. Purely presentational — every page passes its own values and its
// own badge / button JSX for the footer, so behaviour and styles of those
// controls are unchanged from the desktop table.
//
// Used by: ArtistDetail, FestivalDetail, MemberDetail (both sub-tabs),
// PromoterDetail, VenueDetail, PublicProfile, MyShows.
// Desktop tables are untouched.

import React from 'react';
import { Link } from 'react-router-dom';

// "Saturday, May 25, 2002" — UTC, matching how every Show Archive formats
// event_date (date-only values serialized at UTC midnight).
export function formatShowDateLong(eventDate) {
    if (!eventDate) return null;
    const d = new Date(eventDate);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
}

/**
 * Props (all optional unless noted):
 *   title        node   — header text, e.g. "Show #5" or a date (required)
 *   titleTo      string — link target for the title (usually the event)
 *   headerExtra  node   — rendered beside the title (e.g. role pills)
 *   date         string — event_date; rendered as the long date line
 *   eventTitle   string — event title line (linked to titleTo when given)
 *   venueName    string
 *   venueTo      string — venue link target
 *   location     string — "City, State, Country"; joined after the venue
 *   stageName    string — "Stage: X" line (Venue page, in place of the venue)
 *   festivalName string
 *   festivalTo   string
 *   lineup       string — comma-separated artist names
 *   lineupLabel  string — optional prefix such as "With"
 *   extraLines   node[] — further lines under the lineup (e.g. Credit)
 *   footer       node   — action row below the divider
 */
export default function ShowArchiveCard({
    title, titleTo, headerExtra, date, eventTitle,
    venueName, venueTo, location, stageName,
    festivalName, festivalTo, lineup, lineupLabel, extraLines, footer,
}) {
    const longDate = formatShowDateLong(date);
    const hasVenueLine = venueName || location;

    return (
        <div style={S.card}>
            <div style={S.header}>
                {titleTo
                    ? <Link to={titleTo} style={S.titleLink}>{title}</Link>
                    : <span style={S.titleText}>{title}</span>}
                {headerExtra && <span style={S.headerExtra}>{headerExtra}</span>}
            </div>

            {longDate && <div style={S.date}>{longDate}</div>}

            {eventTitle && (
                <div style={S.eventTitle}>
                    {titleTo ? <Link to={titleTo} style={S.purpleLink}>{eventTitle}</Link> : eventTitle}
                </div>
            )}

            {hasVenueLine && (
                <div style={S.metaLine}>
                    {venueName && (venueTo
                        ? <Link to={venueTo} style={S.venueLink}>{venueName}</Link>
                        : <span style={S.venueName}>{venueName}</span>)}
                    {location && <span style={S.location}>{venueName ? ' in ' : ''}{location}</span>}
                </div>
            )}

            {stageName && (
                <div style={S.metaLine}>
                    <span style={S.location}>Stage: </span><span style={S.venueName}>{stageName}</span>
                </div>
            )}

            {festivalName && (
                <div style={S.festivalLine}>
                    {festivalTo ? <Link to={festivalTo} style={S.festivalLink}>{festivalName}</Link> : festivalName}
                </div>
            )}

            {lineup && (
                <div style={S.lineup}>
                    {lineupLabel && <span style={S.lineupLabel}>{lineupLabel} </span>}
                    {lineup}
                </div>
            )}

            {extraLines && extraLines.filter(Boolean).map((line, i) => (
                <div key={i} style={S.extraLine}>{line}</div>
            ))}

            {footer && <div style={S.footer}>{footer}</div>}
        </div>
    );
}

// Thin wrapper so pages don't each need list spacing styles.
export function ShowArchiveCardList({ children }) {
    return <div style={S.list}>{children}</div>;
}

// Spread over a page's own Attended? button / role badge styles inside a
// card footer, so the control is a comfortable tap target on a phone (the
// desktop table versions are 12px). Sizing only — colours stay per page.
export const cardControl = {
    fontSize: '14px',
    padding: '8px 16px',
    borderRadius: '6px',
    display: 'inline-block',
    lineHeight: '1.2',
};

// Joins city / state / country, skipping blanks.
export function joinLocation(...parts) {
    return parts.filter(p => p && String(p).trim()).join(', ');
}

const S = {
    list: { display: 'flex', flexDirection: 'column', gap: '12px' },
    card: {
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '16px',
    },
    header: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        fontSize: '15px',
    },
    titleLink: { color: '#bb86fc', fontWeight: 'bold', textDecoration: 'none' },
    titleText: { color: '#bb86fc', fontWeight: 'bold' },
    headerExtra: { display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#ccc' },
    date: { fontSize: '15px', fontWeight: '600', color: '#e0e0e0', marginBottom: '4px' },
    eventTitle: { fontSize: '14px', marginBottom: '3px' },
    purpleLink: { color: '#bb86fc', textDecoration: 'none' },
    metaLine: { fontSize: '14px', color: '#ccc', marginBottom: '3px', lineHeight: '1.4' },
    venueLink: { color: '#bb86fc', fontWeight: 'bold', textDecoration: 'none' },
    venueName: { color: '#e0e0e0' },
    location: { color: '#aaa' },
    festivalLine: { fontSize: '14px', marginBottom: '3px' },
    festivalLink: { color: '#bb86fc', fontWeight: 'bold', textDecoration: 'none' },
    lineup: {
        fontSize: '15px',
        fontWeight: 'bold',
        color: '#e0e0e0',
        marginTop: '8px',
        lineHeight: '1.5',
        overflowWrap: 'anywhere',
    },
    lineupLabel: { color: '#aaa', fontWeight: 'normal' },
    extraLine: { fontSize: '14px', color: '#ccc', marginTop: '6px' },
    footer: {
        borderTop: '1px solid #333',
        marginTop: '12px',
        paddingTop: '12px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px 16px',
    },
};
